import type { Book } from '../../domain/entities/types';
import { progressPercentage } from '../../utils/core';

export type LibraryOrder = 'recent' | 'title' | 'author' | 'created' | 'finished' | 'rating' | 'progress';

type CompletionSortKey = readonly [known: number, year: number, month: number, day: number, precision: number];

const completionSortKey = (book: Book): CompletionSortKey => {
  if (book.completionOrigin === 'historical' && book.historicalCompletion) {
    const completion = book.historicalCompletion;
    if (completion.precision === 'day') {
      return [1, Number(completion.date.slice(0, 4)), Number(completion.date.slice(5, 7)), Number(completion.date.slice(8, 10)), 3];
    }
    if (completion.precision === 'month') return [1, completion.year, completion.month, -1, 2];
    if (completion.precision === 'year') return [1, completion.year, -1, -1, 1];
    return [0, 0, 0, 0, 0];
  }
  if (book.finishedOn) {
    return [1, Number(book.finishedOn.slice(0, 4)), Number(book.finishedOn.slice(5, 7)), Number(book.finishedOn.slice(8, 10)), 3];
  }
  return [0, 0, 0, 0, 0];
};

const compareCompletion = (a: Book, b: Book): number => {
  const first = completionSortKey(a);
  const second = completionSortKey(b);
  for (let index = 0; index < first.length; index += 1) {
    const difference = (second[index] ?? 0) - (first[index] ?? 0);
    if (difference !== 0) return difference;
  }
  return b.updatedAt.localeCompare(a.updatedAt);
};

export const compareBooks = (order: LibraryOrder) => (a: Book, b: Book): number => {
  if (order === 'title') return a.normalizedTitle.localeCompare(b.normalizedTitle);
  if (order === 'author') return a.normalizedAuthor.localeCompare(b.normalizedAuthor);
  if (order === 'created') return b.createdAt.localeCompare(a.createdAt);
  if (order === 'finished') return compareCompletion(a, b);
  if (order === 'rating') return (b.rating ?? 0) - (a.rating ?? 0);
  if (order === 'progress') return (progressPercentage(b) ?? -1) - (progressPercentage(a) ?? -1);
  return b.updatedAt.localeCompare(a.updatedAt);
};
